type TcData = {
  eventStatus?: string;
  tcString?: string;
  listenerId?: number;
  gdprApplies?: boolean;
  purpose?: { consents?: Record<string, boolean> };
};

export type ConsentInfo = {
  tcString: string;
  gdprApplies: boolean;
  purpose1: boolean;
};

type TcfApi = (
  command: string,
  version: number,
  callback: (tcData: TcData) => void,
  parameter?: unknown,
) => void;

declare global {
  interface Window {
    __tcfapi?: TcfApi;
    ENV?: string;
    ppid?: string;
  }
}

const PPID_COOKIE = 'gpt_ppid50';
const PPID_CHARS =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined' || !document.cookie) return undefined;
  if (document.cookie.indexOf(name) === -1) return undefined;
  const parts = `; ${document.cookie}`.split(`; ${name}=`);
  if (parts.length !== 2) return undefined;
  const value = parts.pop()?.split(';').shift()?.trim();
  return value || undefined;
}

export function waitForConsent(timeoutMs: number): Promise<ConsentInfo | null> {
  return new Promise((resolve) => {
    const tcfapi = typeof window !== 'undefined' ? window.__tcfapi : undefined;
    if (typeof tcfapi !== 'function') {
      resolve(null);
      return;
    }

    let settled = false;
    let listenerId: number | undefined;

    const finish = (value: ConsentInfo | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (listenerId !== undefined) {
        tcfapi('removeEventListener', 2, () => {}, listenerId);
      }
      resolve(value);
    };

    const timer = setTimeout(() => finish(null), timeoutMs);

    tcfapi('addEventListener', 2, (tcData: TcData) => {
      if (typeof tcData?.listenerId === 'number') {
        listenerId = tcData.listenerId;
      }
      if (tcData == null || typeof tcData.eventStatus === 'undefined') return;
      if (
        tcData.eventStatus !== 'tcloaded' &&
        tcData.eventStatus !== 'useractioncomplete'
      ) {
        return;
      }
      const tcString =
        typeof tcData.tcString === 'string' ? tcData.tcString.trim() : '';
      if (!tcString) {
        finish(null);
        return;
      }
      const gdprApplies = tcData.gdprApplies !== false;
      finish({
        tcString,
        gdprApplies,
        purpose1: !gdprApplies || tcData.purpose?.consents?.['1'] === true,
      });
    });
  });
}

export function getOrCreatePpid(): string | undefined {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return undefined;
  }

  const existing = (getCookie(PPID_COOKIE) || window.ppid || '').trim();
  if (existing) return existing;

  const rnd = new Uint32Array(50);
  crypto.getRandomValues(rnd);
  let id = '';
  for (const n of rnd) id += PPID_CHARS[n % PPID_CHARS.length];

  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${PPID_COOKIE}=${id};expires=${expires.toUTCString()};path=/`;
  window.ppid = id;
  return id;
}
